import React, { useMemo, useState } from 'react';
import './addGlWellTestModal.scss';
import ModalHeader from '../../components/common/ModalHeader';
import FormField from '../../components/common/FormField';
import CommentSection from '../../components/common/CommentSection';
import ActionButtons from '../../components/common/ActionButtons';
import { saveWellTestAsync } from '../add-well-test/services/addWellTestSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { fetchWellTestData } from '../../dispatchers/WellTestDispatcher';
import useConfig from '../../hooks/useConfig';
import { createPortal } from 'react-dom';

interface FormErrors {
  [key: string]: string;
}

interface TouchedFields {
  [key: string]: boolean;
}

interface AddGLWellTestModalProps {
  modalTitle: string;
  buttonText: string;
  onClose: () => void;
  initialData?: Partial<any>;
  disabled?: boolean;
  installNumber: number | null;
}

export default function AddGLWellTestModal({
  modalTitle,
  buttonText,
  onClose,
  initialData,
  installNumber,
}: AddGLWellTestModalProps) {
  const [formData, setFormData] = useState({
    date: '',
    enabled: true,
    oil: '',
    water: '',
    gas: '',
    tubingHeadPressure: '',
    wellheadTemperature: '',
    casingPressure: '',
    flowlinePressure: '',
    productionChokePosition: '',
    downholePressure: '',
    downholeTemperature: '',
    injectionRate: '',
    injectionChoke: '',
    waterDensity: '',
    comment: '',
    id: 0,
    pumpIntakePressure: '',
    pumpIntakeTemperature: '',
    pumpFrequency: '',
    pumpDischargePressure: '',
    liquidFlowrate: '',
    waterCut: '',
    totalGasOilRatio: '',
    wellheadPressure: '',
    separatorPressure: '',
    separatorTemperature: '',
    bottomholePressure: '',
    glr: '',
    oilGasInjectionRate: '',
    reservoirPressure: '',
    pwf: '',
    productivityIndex: '',
    flowType: '',

    ...initialData,
  });

  const dispatch = useAppDispatch();
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const testWellData = useAppSelector((state: any) => state.testWellDataSlice.data);
  const wellTestIdData = useAppSelector((state: any) => state.WellTestIdSlice?.data);
  const { enablePumpChecker } = useConfig();

  const disabledDates = useMemo(
    () =>
      (testWellData ?? []).map((item: any) => {
        const date = new Date(item.Date);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }),
    [testWellData],
  );

  const isEditMode = Boolean(initialData?.id);

  const mandatoryFields = ['date', 'oil', 'water', 'gas', 'tubingHeadPressure', 'waterDensity'];
  const validateField = (name: string, value: string): string => {
    if (mandatoryFields.includes(name) && value === '') return 'This field is required';
    if (value === '') return '';
    const numeric = (v: string, min: number, max: number, msg: string) => {
      if (!/^\d*\.?\d+$/.test(v)) return msg;
      const n = parseFloat(v);
      if (isNaN(n) || n < min || n > max) return msg;
      return null;
    };
    switch (name) {
      case 'oil':
        return numeric(value, 0, 10000, 'Must be between 0 and 10000 stb/d') || '';
      case 'water':
        return numeric(value, 0, 20000, 'Must be between 0 and 20000 stb/d') || '';
      case 'gas':
        return numeric(value, 0, 15000, 'Must be between 0 and 15000 mscf/d') || '';
      case 'tubingHeadPressure':
        return numeric(value, 1e-14, 5000, 'Must be between 1e-14 and 5000 psig') || '';
      case 'wellheadTemperature':
        return numeric(value, 50, 400, 'Must be between 50 and 400 deg F') || '';
      case 'casingPressure':
        return numeric(value, 1e-14, 5000, 'Must be between 1e-14 and 5000 psig') || '';
      case 'flowlinePressure':
        return numeric(value, 1e-14, 5000, 'Must be between 1e-14 and 5000 psig') || '';
      case 'productionChokePosition':
        return numeric(value, 0, 100, 'Must be between 0 and 100 %') || '';
      case 'downholePressure':
        return numeric(value, 0, 5000, 'Must be between 0 and 5000 psig') || '';
      case 'downholeTemperature':
        return numeric(value, 0, 300, 'Must be between 0 and 300 deg F') || '';
      case 'injectionRate':
        return numeric(value, 0, 20000, 'Must be between 0 and 20000 mscf/d') || '';
      case 'injectionChoke':
        return numeric(value, 0, 100, 'Must be between 0 and 100 %') || '';
      case 'waterDensity':
        return numeric(value, 0.9, 1.6, 'Must be between 0.9 and 1.6 s.g.') || '';
      case 'reservoirPressure':
        return numeric(value, 300, 20000, 'Must be between 300 and 20000 psig') || '';
      default:
        return '';
    }
  };

  const handleInputChange = (name: string, value: string | number | Date | null) => {
    let processed: string | number | null = null;
    if (value instanceof Date) processed = value.toISOString();
    else if (value === null) processed = '';
    else processed = value;
    setFormData((p) => ({ ...p, [name]: processed }));
    if (!touched[name]) setTouched((p) => ({ ...p, [name]: true }));
    const err = validateField(name, String(processed ?? ''));
    setErrors((prev) =>
      err
        ? { ...prev, [name]: err }
        : (() => {
            const n = { ...prev };
            delete n[name];
            return n;
          })(),
    );
  };
  const formatValue = (v: string | number | null) => (v === null ? null : typeof v === 'number' ? v.toString() : v);
  const handleBlur = (n: string) => {
    if (!touched[n]) setTouched({ ...touched, [n]: true });
    if (mandatoryFields.includes(n) && formData[n as keyof typeof formData] === '')
      setErrors({ ...errors, [n]: 'This field is required' });
  };
  const handleCheckboxChange = () => setFormData((p) => ({ ...p, enabled: !p.enabled }));

  const validateForm = () => {
    const newErrs = { ...errors };
    let ok = true;
    mandatoryFields.forEach((f) => {
      if (formData[f as keyof typeof formData] === '') {
        newErrs[f] = 'This field is required';
        ok = false;
      }
    });
    setErrors(newErrs);
    return ok;
  };
  const [isModalOpen, setIsModalOpen] = useState(true);
  const handleCancel = () => {
    setIsModalOpen(false);
    onClose();
  };

  const handleConfirmSave = async () => {
    const newTouched = { ...touched };
    mandatoryFields.forEach((f) => (newTouched[f] = true));
    newTouched['reservoirPressure'] = true;
    setTouched(newTouched);
    if (!validateForm()) return;
    setIsModalOpen(false);
    onClose();
    setSubmitError('');
    setSubmitSuccess(false);
    try {
      setIsSubmitting(true);
      const updated = {
        ...formData,
        wellId: wellTestIdData?.WellId ?? null,
        methodOfProduction: wellTestIdData?.MethodOfProduction ?? null,
        InjectionRate: formData.injectionRate ?? null,
        InjectionChoke: formData.injectionChoke ?? null,
        liquidFlowrate: '',
        waterCut: '',
        totalGasOilRatio: '',
        wellheadPressure: formData.tubingHeadPressure ?? '',
        separatorPressure: '',
        separatorTemperature: '',
        bottomholePressure: '',
        glr: '',
        casingPressure: formData.casingPressure ?? '',
        pumpIntakePressure: formData.pumpIntakePressure ?? '',
        pumpIntakeTemperature: formData.pumpIntakePressure ?? '',
        pumpFrequency: formData.pumpFrequency ?? '',
        pumpDischargePressure: formData.pumpDischargePressure ?? '',
        reservoirPressure: formData.reservoirPressure ?? '',
      };
      const res = await dispatch(saveWellTestAsync(updated));
      if (saveWellTestAsync.fulfilled.match(res)) {
        setSubmitSuccess(true);
        if (wellTestIdData) dispatch(fetchWellTestData({ wellId: wellTestIdData.WellId, installNumber }));
      } else setSubmitError(res.payload as string);
    } catch {
      setSubmitError('Unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isModalOpen) return null;

  return createPortal(
    <div className='modal-overlay'>
      <div className='modal-container'>
        <div className='modal-main-container'>
          <ModalHeader title={modalTitle} onClose={handleCancel} />

          <main className='form-main'>
            <section className='form-section'>
              {submitSuccess && <div className='success-message'>Well test data saved successfully!</div>}
              {submitError && <div className='error-message'>{submitError}</div>}

              <div className='date-selector-container'>
                <FormField
                  name='date'
                  value={formData.date ? new Date(formData.date) : null}
                  onChange={(n, v) => {
                    if (v) {
                      const d = v as Date;
                      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
                        d.getDate(),
                      ).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(
                        2,
                        '0',
                      )}:${String(d.getSeconds()).padStart(2, '0')}.${String(d.getMilliseconds()).slice(0, 1)}`;
                      handleInputChange(n, iso);
                    } else handleInputChange(n, '');
                  }}
                  error={touched.date ? errors.date : ''}
                  type='date'
                  required
                  disabled={!!initialData?.id}
                  disabledDates={disabledDates}
                />
              </div>

              <div className='enabled-container'>
                <div className='checkbox-container'>
                  <div className={`checkbox ${formData.enabled ? 'enabled' : ''}`} onClick={handleCheckboxChange}>
                    {formData.enabled && <div className='checkbox-icon' />}
                  </div>
                </div>
                <div className='enabled-text-container' onClick={handleCheckboxChange}>
                  <span className='enabled-text'>Enabled</span>
                </div>
              </div>

              <div className='form-row'>
                <FormField
                  label='Oil (stb/d)'
                  name='oil'
                  value={formatValue(formData.oil)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 0 and 10000 stb/d'
                  error={touched.oil ? errors.oil : ''}
                  required
                />
                <FormField
                  label='Water (stb/d)'
                  name='water'
                  value={formatValue(formData.water)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 0 and 20000 stb/d'
                  error={touched.water ? errors.water : ''}
                  required
                />
                <FormField
                  label='Gas (mscf/d)'
                  name='gas'
                  value={formatValue(formData.gas)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 0 and 15000 mscf/d'
                  error={touched.gas ? errors.gas : ''}
                  required
                />
              </div>

              <div className='form-row'>
                <FormField
                  label='Tubing Head Pressure (psig)'
                  name='tubingHeadPressure'
                  value={formatValue(formData.tubingHeadPressure)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 1e-14 and 5000 psig'
                  error={touched.tubingHeadPressure ? errors.tubingHeadPressure : ''}
                  required
                />
                <FormField
                  label='Wellhead Temperature (deg F)'
                  name='wellheadTemperature'
                  value={formatValue(formData.wellheadTemperature)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 50 and 400 deg F'
                  error={touched.wellheadTemperature ? errors.wellheadTemperature : ''}
                />
                <FormField
                  label='Casing Head Pressure (psig)'
                  name='casingPressure'
                  value={formatValue(formData.casingPressure)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 1e-14 and 5000 psig'
                />
              </div>

              <div className='form-row'>
                <FormField
                  label='Flowline Pressure (psig)'
                  name='flowlinePressure'
                  value={formatValue(formData.flowlinePressure)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 1e-14 and 5000 psig'
                  error={touched.flowlinePressure ? errors.flowlinePressure : ''}
                />
                <FormField
                  label='Production Choke Position (%)'
                  name='productionChokePosition'
                  value={formatValue(formData.productionChokePosition)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 0 and 100 %'
                  error={touched.productionChokePosition ? errors.productionChokePosition : ''}
                />
                <FormField
                  label='Downhole Pressure (psig)'
                  name='downholePressure'
                  value={formatValue(formData.downholePressure)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  helpText='Must be between 0 and 5000 psig'
                  error={touched.downholePressure ? errors.downholePressure : ''}
                />
              </div>

              {enablePumpChecker && isEditMode ? (
                <>
                  <div className='form-row'>
                    <FormField
                      label='Downhole Temperature (deg F)'
                      name='downholeTemperature'
                      value={formatValue(formData.downholeTemperature)}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      helpText='Must be between 0 and 300 deg F'
                      error={touched.downholeTemperature ? errors.downholeTemperature : ''}
                    />
                    <FormField
                      label='Pwf'
                      name='pwf'
                      value={formData.pwf || '—'}
                      disabled
                      onChange={undefined as any}
                      containerClassName='calculated-field tooltip-field'
                    />
                    <FormField
                      label='Productivity Index'
                      name='productivityIndex'
                      value={formData.productivityIndex || '—'}
                      disabled
                      onChange={undefined as any}
                      containerClassName='calculated-field tooltip-field'
                    />
                  </div>
                  <div className='form-row'>
                    <FormField
                      label='Gas Injection Rate (mscf/d)'
                      name='injectionRate'
                      value={formatValue(formData.injectionRate)}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      helpText='Must be between 0 and 20000 mscf/d'
                      error={touched.injectionRate ? errors.injectionRate : ''}
                    />
                    <FormField
                      label='Flow Path'
                      name='flowPath'
                      value={formData.flowType || '—'}
                      disabled
                      onChange={undefined as any}
                      containerClassName='calculated-field tooltip-field'
                    />
                    <FormField
                      label='Injection Choke (%)'
                      name='injectionChoke'
                      value={formatValue(formData.injectionChoke)}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      helpText='Must be between 0 and 100 %'
                      error={touched.injectionChoke ? errors.injectionChoke : ''}
                    />
                  </div>
                </>
              ) : (
                <div className='form-row'>
                  <FormField
                    label='Downhole Temperature (deg F)'
                    name='downholeTemperature'
                    value={formatValue(formData.downholeTemperature)}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    helpText='Must be between 0 and 300 deg F'
                    error={touched.downholeTemperature ? errors.downholeTemperature : ''}
                  />
                  <FormField
                    label='Gas Injection Rate (mscf/d)'
                    name='injectionRate'
                    value={formatValue(formData.injectionRate)}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    helpText='Must be between 0 and 20000 mscf/d'
                    error={touched.injectionRate ? errors.injectionRate : ''}
                  />
                  <FormField
                    label='Injection Choke (%)'
                    name='injectionChoke'
                    value={formatValue(formData.injectionChoke)}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    helpText='Must be between 0 and 100 %'
                    error={touched.injectionChoke ? errors.injectionChoke : ''}
                  />
                </div>
              )}
              <div className='form-row split-row'>
                <div className='wide-input'>
                  <FormField
                    label='Water Density (s.g.)'
                    name='waterDensity'
                    value={formatValue(formData.waterDensity)}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    helpText='Must be between 0.9 and 1.6 s.g.'
                    error={touched.waterDensity ? errors.waterDensity : ''}
                    required
                  />
                </div>
                <div className='wide-input'>
                  <FormField
                    label='Reservoir Pressure (psig)'
                    name='reservoirPressure'
                    value={formatValue(formData.reservoirPressure)}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    helpText='Must be between 300 and 20000 psig'
                    error={touched.reservoirPressure ? errors.reservoirPressure : ''}
                  />
                </div>
              </div>
              <CommentSection value={formData.comment} onChange={(v) => handleInputChange('comment', v)} />
            </section>
          </main>

          <footer className='form-footer'>
            <ActionButtons
              buttonText={buttonText}
              onSubmit={handleConfirmSave}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
            />
          </footer>
        </div>
      </div>
    </div>,
    document.querySelector('.outlet-container') || document.body,
  );
}
